import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Pencil, Check, X } from 'lucide-react';

/** Debounce delay for save mutations (F004 — #586 AC003) */
export const DEBOUNCE_DELAY_MS = 350;

export type CellType = 'currency' | 'text' | 'number';

export interface InlineEditableCellProps {
  value: string | number | null | undefined;
  onSave: (newValue: string | number) => Promise<void>;
  cellType?: CellType;
  placeholder?: string;
  disabled?: boolean;
  suffix?: string;
  prefix?: string;
  maxLength?: number;
  className?: string;
}

/**
 * Base inline editable cell component
 *
 * Features:
 * - Click to edit (display -> input)
 * - Auto-save on blur or Enter key
 * - Cancel on ESC key
 * - Optimistic updates with loading state
 * - Error handling with revert
 *
 * States:
 * - Display: Formatted value with hover indicator
 * - Edit: Raw input field with cursor
 * - Saving: Spinner during mutation
 * - Saved: Brief checkmark confirmation
 */
export const InlineEditableCell: React.FC<InlineEditableCellProps> = ({
  value,
  onSave,
  cellType = 'text',
  placeholder = 'Click to edit',
  disabled = false,
  suffix,
  prefix: _prefix,
  maxLength,
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string | number | undefined>(value ?? undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Format value for display
  const formatDisplayValue = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined || val === '') {
      return placeholder;
    }

    if (cellType === 'currency') {
      const numVal = typeof val === 'string' ? parseFloat(val) : val;
      if (isNaN(numVal)) return placeholder;
      return `$${numVal.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    if (cellType === 'number') {
      const numVal = typeof val === 'string' ? parseFloat(val) : val;
      if (isNaN(numVal)) return placeholder;
      return numVal.toLocaleString('en-AU');
    }

    return String(val);
  };

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value ?? undefined);
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select?.();
      }, 50);
    }
  }, [isEditing]);

  const handleEnterEditMode = () => {
    if (disabled) return;
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setEditValue(value ?? undefined);
    setIsEditing(false);
    setError(null);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const executeSave = useCallback(async (valueToSave: string | number) => {
    setIsSaving(true);
    setError(null);

    try {
      await onSave(valueToSave);
      setIsEditing(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Save failed';
      setError(message);
      // Revert on error
      setEditValue(value ?? undefined);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, value]);

  const handleSave = useCallback(() => {
    // Skip save if value hasn't changed
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    // Validate required fields
    if (editValue === null || editValue === undefined || editValue === '') {
      setError('Value is required');
      return;
    }

    // Debounce: cancel any pending save and schedule a new one
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      executeSave(editValue);
    }, DEBOUNCE_DELAY_MS);
  }, [editValue, value, executeSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const displayValue = formatDisplayValue(value);
  const isPlaceholder = value === null || value === undefined || value === '';

  // Display mode
  if (!isEditing) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`inline-editable-cell ${className} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} group relative px-2 py-1 rounded hover:bg-gray-50 transition-colors ${
                isPlaceholder ? 'text-gray-400 italic' : ''
              } ${error ? 'border border-red-300 bg-red-50' : ''}`}
              onClick={handleEnterEditMode}
            >
              <span className="select-none">{displayValue}</span>
              {!disabled && (
                <>
                  {showSaved ? (
                    <Check className="ml-2 h-4 w-4 inline text-green-500" />
                  ) : (
                    <Pencil className="ml-2 h-4 w-4 inline opacity-0 group-hover:opacity-50 transition-opacity text-gray-400" />
                  )}
                </>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{error || (disabled ? 'Editing disabled' : 'Click to edit')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Edit mode
  return (
    <div className="inline-editable-cell-editing flex items-center gap-1">
      {cellType === 'currency' || cellType === 'number' ? (
        <div className="relative w-full">
          {cellType === 'currency' && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
          )}
          <Input
            ref={inputRef}
            type="number"
            value={editValue as number}
            onChange={(e) => setEditValue(e.target.value ? parseFloat(e.target.value) : undefined)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            placeholder={placeholder}
            className={`w-full ${cellType === 'currency' ? 'pl-6' : ''}`}
            step="0.01"
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">{suffix}</span>
          )}
        </div>
      ) : (
        <Input
          ref={inputRef}
          value={editValue as string}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          placeholder={placeholder}
          maxLength={maxLength}
          className="w-full"
        />
      )}

      {error && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <X className="h-4 w-4 text-red-500 cursor-pointer" />
            </TooltipTrigger>
            <TooltipContent>
              <p>{error}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
